import { Layout } from "@/components/layout";
import { Link, useLocation } from "wouter";
import { useLanguage } from "@/contexts/language";
import { useGetWeaknesses, getGetWeaknessesQueryKey, useCreateSession } from "@workspace/api-client-react";
import { useTrack } from "@/contexts/track";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Target, AlertTriangle, Lock, Crown, CheckCircle2,
  Bookmark, Info, X, PauseCircle,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useSubscription } from "@/contexts/subscription";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useAccountPaused } from "@/hooks/useAccountPaused";

const BOOKMARK_KEY = "cpl_saved_questions";

function loadBookmarks(): number[] {
  try {
    const raw = localStorage.getItem(BOOKMARK_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    console.warn("[weaknesses] Failed to read bookmarks from storage:", err);
    return [];
  }
}

function removeBookmark(id: number) {
  try {
    const current = loadBookmarks().filter(b => b !== id);
    localStorage.setItem(BOOKMARK_KEY, JSON.stringify(current));
  } catch (err) {
    console.warn("[weaknesses] Failed to persist bookmark removal:", err);
  }
}

type Tab = "weakness" | "saved";

export default function Turbulence() {
  const [, setLocation] = useLocation();
  const { getPlanForTrack } = useSubscription();
  const { selectedTrack } = useTrack();
  const plan = getPlanForTrack(selectedTrack);
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const w = t.weaknesses;
  const [tab, setTab] = useState<Tab>("weakness");
  const [bookmarks, setBookmarks] = useState<number[]>([]);
  const { paused, pauseExpiresAt, loading: pauseLoading } = useAccountPaused();

  const isProPlus = plan === "pro_plus";
  const isPro = plan === "pro" || plan === "pro_plus";

  useEffect(() => {
    setBookmarks(loadBookmarks());
  }, [tab]);

  const { data: weaknesses, isLoading } = useGetWeaknesses({ limit: 50, track: selectedTrack }, {
    query: {
      queryKey: getGetWeaknessesQueryKey({ limit: 50, track: selectedTrack }),
      staleTime: 300_000,
      enabled: isProPlus,
    }
  });

  const createSession = useCreateSession();

  const handleDrillWeaknesses = () => {
    if (!weaknesses || weaknesses.length === 0) return;
    // In FR mode only drill questions that have a full French translation
    const eligible = language === "fr"
      ? weaknesses.filter(e => (e.question as { textFr?: string }).textFr)
      : weaknesses;
    const ids = eligible.slice(0, 20).map(e => e.question.id);
    if (ids.length === 0) return;
    createSession.mutate({
      data: { mode: "weakness", questionIds: ids, licenseTrack: selectedTrack }
    }, {
      onSuccess: (session) => setLocation(`/session/${session.id}`)
    });
  };

  const handleDrillSingle = (id: number) => {
    createSession.mutate({
      data: { mode: "weakness", questionIds: [id], licenseTrack: selectedTrack }
    }, {
      onSuccess: (session) => setLocation(`/session/${session.id}`)
    });
  };

  const handleDrillSaved = () => {
    if (bookmarks.length === 0) {
      toast({ title: w.noSavedToastTitle, description: w.noSavedToastDesc, variant: "destructive" });
      return;
    }
    createSession.mutate({
      data: { mode: "saved", questionIds: bookmarks, licenseTrack: selectedTrack }
    }, {
      onSuccess: (session) => setLocation(`/session/${session.id}`)
    });
  };

  const handleRemoveBookmark = (id: number) => {
    removeBookmark(id);
    setBookmarks(prev => prev.filter(b => b !== id));
    toast({ title: w.bookmarkRemovedTitle, description: w.bookmarkRemovedDesc });
  };

  if (!pauseLoading && paused) {
    const resumeDate = pauseExpiresAt ? new Date(pauseExpiresAt).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : null;
    return (
      <Layout>
        <div className="max-w-2xl mx-auto flex flex-col items-center justify-center min-h-[50vh] text-center gap-5 animate-in fade-in duration-500">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30">
            <PauseCircle className="w-8 h-8 text-amber-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold font-mono tracking-tight text-foreground mb-2">{w.pausedTitle}</h2>
            <p className="text-sm text-muted-foreground font-mono max-w-sm">
              {w.pausedBody}
              {resumeDate ? ` ${w.pausedResumesOn.replace("{date}", resumeDate)}` : ""}
            </p>
          </div>
          <p className="text-xs text-muted-foreground/60 font-mono">{w.pausedSafe}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-in fade-in duration-500">

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">{w.title}</h1>
            <p className="text-muted-foreground">{w.subtitle}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border gap-0">
          <button
            onClick={() => setTab("weakness")}
            className={cn(
              "px-5 py-2.5 text-sm font-medium font-mono transition-colors border-b-2 -mb-px",
              tab === "weakness"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <span className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              {w.tabWeakness}
              {!isProPlus && (
                <span className="text-[9px] font-bold text-amber-400 bg-amber-500/20 border border-amber-500/30 rounded px-1 py-0.5">Pro+</span>
              )}
            </span>
          </button>
          <button
            onClick={() => setTab("saved")}
            className={cn(
              "px-5 py-2.5 text-sm font-medium font-mono transition-colors border-b-2 -mb-px",
              tab === "saved"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <span className="flex items-center gap-2">
              <Bookmark className="w-4 h-4" />
              {w.tabSaved}
              {bookmarks.length > 0 && (
                <span className="text-[10px] font-bold text-primary bg-primary/20 border border-primary/30 rounded px-1.5 py-0.5">
                  {bookmarks.length}
                </span>
              )}
              {!isPro && (
                <span className="text-[9px] font-bold text-amber-400 bg-amber-500/20 border border-amber-500/30 rounded px-1 py-0.5">Pro</span>
              )}
            </span>
          </button>
        </div>

        {/* ── WEAKNESS TAB ──────────────────────────────────────────────────────── */}
        {tab === "weakness" && (
          <>
            <div className="flex justify-end">
              {!isProPlus ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-bold font-mono px-3 py-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300">
                  <Crown className="w-3.5 h-3.5" />PRO PLUS ADVANCED
                </span>
              ) : (
                <Button
                  onClick={handleDrillWeaknesses}
                  disabled={!weaknesses || weaknesses.length === 0 || createSession.isPending}
                  className="w-full md:w-auto"
                >
                  <Target className="w-4 h-4 mr-2" />
                  {w.flyIntoTurbulence}
                </Button>
              )}
            </div>

            {!isProPlus ? (
              <div className="relative">
                <div className="space-y-4 select-none pointer-events-none blur-sm opacity-30" aria-hidden="true">
                  {[
                    { cat: "Navigation", text: "A pilot wants to fly from point A to point B. Given the wind...", err: 82 },
                    { cat: "Meteorology", text: "When a warm front passes, which type of precipitation is most common?", err: 71 },
                    { cat: "Air Law", text: "Under CAR 602, what is the minimum altitude over a built-up area?", err: 67 },
                  ].map(({ cat, text, err }, i) => (
                    <Card key={i} className="border-border bg-card">
                      <CardContent className="p-6 flex flex-col md:flex-row gap-6 items-start">
                        <div className="flex-1 space-y-2">
                          <span className="text-xs font-mono text-muted-foreground uppercase bg-secondary px-2 py-1 rounded">{cat}</span>
                          <h3 className="font-medium text-lg leading-snug">{text}</h3>
                        </div>
                        <div className="shrink-0 w-full md:w-64 space-y-3 border-t md:border-t-0 md:border-l border-border pt-4 md:pt-0 md:pl-6">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Error Rate</span>
                            <span className="font-mono font-bold text-destructive">{err}%</span>
                          </div>
                          <Progress value={err} className="h-2" indicatorClassName="bg-destructive" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-card border border-amber-400/30 rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl text-center">
                    <div className="flex items-center justify-center w-14 h-14 rounded-full bg-amber-400/15 border border-amber-400/30 mx-auto mb-4">
                      <Lock className="w-7 h-7 text-amber-300" />
                    </div>
                    <h2 className="text-lg font-bold text-foreground mb-1">{w.weaknessLockTitle}</h2>
                    <p className="text-xs font-mono text-amber-300 tracking-widest mb-4">{w.weaknessLockBadge}</p>
                    <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                      {w.weaknessLockBody}
                      {plan === "pro" && (
                        <span className="block mt-2 text-primary/80">
                          {w.weaknessProNote}
                        </span>
                      )}
                    </p>
                    <Link
                      href="/subscription"
                      className="inline-flex items-center justify-center gap-2 w-full py-3 bg-amber-400 hover:bg-amber-300 text-black text-sm font-bold rounded transition-colors"
                    >
                      <Crown className="w-4 h-4" />
                      {w.upgradeBtn}
                    </Link>
                  </div>
                </div>
              </div>
            ) : isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-32 w-full" />)}
              </div>
            ) : weaknesses && weaknesses.length > 0 ? (
              <div className="space-y-4">
                {weaknesses.map((entry) => (
                  <Card key={entry.question.id} className="border-border bg-card hover:border-primary/50 transition-colors">
                    <CardContent className="p-6 flex flex-col md:flex-row gap-6 items-start md:items-center">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-muted-foreground uppercase bg-secondary px-2 py-1 rounded">
                            {entry.question.category}
                          </span>
                          {entry.errorRate > 70 && (
                            <span className="text-xs font-mono text-destructive uppercase bg-destructive/10 px-2 py-1 rounded flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> CRITICAL
                            </span>
                          )}
                        </div>
                        <h3 className="font-medium text-lg leading-snug">
                          {language === "fr" ? ((entry.question as { textFr?: string }).textFr ?? entry.question.text) : entry.question.text}
                        </h3>
                      </div>
                      <div className="shrink-0 w-full md:w-64 space-y-3 border-t md:border-t-0 md:border-l border-border pt-4 md:pt-0 md:pl-6">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{w.errorRateLabel}</span>
                          <span className="font-mono font-bold text-destructive">{Math.round(entry.errorRate)}%</span>
                        </div>
                        <Progress value={entry.errorRate} className="h-2" indicatorClassName="bg-destructive" />
                        <div className="flex justify-between text-xs text-muted-foreground font-mono">
                          <span>{w.wrongLabel}: {entry.timesWrong}</span>
                          <span>{w.totalCountLabel}: {entry.timesAnswered}</span>
                        </div>
                        <div className="border-t border-border pt-3 space-y-1.5">
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground font-mono">{w.masteryLabel}</span>
                            <span className={`font-mono font-bold ${entry.timesCorrect >= 1 ? "text-primary" : "text-muted-foreground"}`}>
                              {entry.timesCorrect}/2
                            </span>
                          </div>
                          <div className="flex gap-1">
                            {[0, 1].map(i => (
                              <div
                                key={i}
                                className={`h-2 flex-1 rounded-full transition-colors ${entry.timesCorrect > i ? "bg-primary" : "bg-secondary"}`}
                              />
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground font-mono">
                            {entry.timesCorrect === 0 ? w.clearTwoMore : w.clearOneMore}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => handleDrillSingle(entry.question.id)}
                        >
                          <Target className="w-3 h-3 mr-2" />
                          {w.flyThis}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-dashed bg-card/50">
                <CardContent className="p-12 flex flex-col items-center justify-center text-center space-y-4">
                  <div className="p-4 bg-primary/10 rounded-full">
                    <Target className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold">{w.clearSkiesTitle}</h3>
                  <p className="text-muted-foreground max-w-sm">{w.clearSkiesBody}</p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ── SAVED QUESTIONS TAB ──────────────────────────────────────────────── */}
        {tab === "saved" && (
          <>
            {!isPro ? (
              <div className="relative mt-4">
                <div className="space-y-4 select-none pointer-events-none blur-sm opacity-30" aria-hidden="true">
                  {[1, 2, 3].map(i => (
                    <Card key={i} className="border-border bg-card">
                      <CardContent className="p-5 flex items-center justify-between">
                        <div className="space-y-1 flex-1">
                          <div className="h-4 bg-secondary rounded w-16" />
                          <div className="h-5 bg-secondary rounded w-full" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-card border border-amber-400/30 rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl text-center">
                    <div className="flex items-center justify-center w-14 h-14 rounded-full bg-amber-400/15 border border-amber-400/30 mx-auto mb-4">
                      <Lock className="w-7 h-7 text-amber-300" />
                    </div>
                    <h2 className="text-lg font-bold text-foreground mb-1">{w.savedLockTitle}</h2>
                    <p className="text-xs font-mono text-amber-300 tracking-widest mb-4">{w.savedLockBadge}</p>
                    <p className="text-sm text-muted-foreground mb-6 leading-relaxed">{w.savedLockBody}</p>
                    <Link
                      href="/subscription"
                      className="inline-flex items-center justify-center gap-2 w-full py-3 bg-amber-400 hover:bg-amber-300 text-black text-sm font-bold rounded transition-colors"
                    >
                      <Crown className="w-4 h-4" />
                      {w.upgradeToPro}
                    </Link>
                  </div>
                </div>
              </div>
            ) : bookmarks.length === 0 ? (
              <Card className="border-dashed bg-card/50">
                <CardContent className="p-12 flex flex-col items-center justify-center text-center space-y-4">
                  <div className="p-4 bg-primary/10 rounded-full">
                    <Bookmark className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold">{w.savedEmptyTitle}</h3>
                  <p className="text-muted-foreground max-w-sm">{w.savedEmptyBody}</p>
                  <div className="flex items-start gap-2 p-3 bg-secondary/40 border border-border rounded-md max-w-sm text-left">
                    <Info className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground">{w.bookmarksPersistNote}</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Header bar with drill all button */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bookmark className="w-4 h-4 text-primary" />
                    <span className="text-sm font-mono font-semibold text-primary">
                      {w.drillNSaved.replace("{n}", String(bookmarks.length))}
                    </span>
                  </div>
                  <Button onClick={handleDrillSaved} disabled={createSession.isPending} size="sm">
                    <Bookmark className="w-3.5 h-3.5 mr-2" />
                    {createSession.isPending ? w.starting : w.drillAllN.replace("{n}", String(bookmarks.length))}
                  </Button>
                </div>

                <div className="flex items-start gap-2 p-3 bg-secondary/40 border border-border rounded-md">
                  <Info className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground">{w.bookmarksNote}</p>
                </div>

                <div className="space-y-2">
                  {bookmarks.map((id, idx) => (
                    <Card key={id} className="border-border bg-card hover:border-primary/40 transition-colors">
                      <CardContent className="p-4 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-mono text-muted-foreground w-6 text-right shrink-0">#{idx + 1}</span>
                          <div>
                            <span className="text-sm font-mono text-foreground">Question ID {id}</span>
                            <p className="text-xs text-muted-foreground">{w.bookmarkedDuring}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveBookmark(id)}
                            className="text-muted-foreground hover:text-destructive h-8 w-8 p-0"
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="pt-2 flex justify-end">
                  <Button onClick={handleDrillSaved} disabled={createSession.isPending}>
                    <Bookmark className="w-4 h-4 mr-2" />
                    {createSession.isPending ? w.starting : w.drillNSaved.replace("{n}", String(bookmarks.length))}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
